import { useCallback } from "react";

import { extractGpsCoordsFromImageBuffer } from "../../../utils/exifUtils";
import {
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
  CATCH_IMAGE_HEIC_UNSUPPORTED_ERROR_CODE,
  CATCH_IMAGE_HEIC_UNSUPPORTED_ERROR_MESSAGE,
  getFilenameExtension,
  inferSelectedMediaType,
} from "./catchMediaConstants";
import {
  HEIC_MIME_TYPES,
  buildStabilizedFileFromBuffer,
  describeSnapshotFileObject,
  getCurrentUserAgent,
  resolveImageMimeTypeFromBuffer,
  snapshotSelectedFile,
} from "./catchMediaPipeline";
import { SUBMIT_PHASES } from "./catchSubmitUtils";

export default function useCatchMediaSelectionFlow({
  selectionIdRef,
  submitTransactionRef,
  snapshotInFlightRef,
  setSubmitStatus,
  t,
  resetSelectionDerivedMediaState,
  setMediaStateForSelection,
  uploadAssetRef,
  rejectImageSelectionWithoutSnapshot,
  finalizeSelectionInputClear,
  setSubmitPhase,
  applySelectedMedia,
  user,
  requestExifConsent,
  setCoordsFromExifIfEmpty,
}) {
  const handleImageUpload = useCallback(
    async (event, type = "image") => {
      const inputEl = event?.target;
      const inputId = typeof inputEl?.id === "string" ? inputEl.id : "";
      const inputAccept =
        typeof inputEl?.accept === "string" ? inputEl.accept : "";
      const inputCapture =
        typeof inputEl?.capture === "string" ? inputEl.capture : "";
      const inputMultiple = Boolean(inputEl?.multiple);
      const inputValueBeforeClear =
        typeof inputEl?.value === "string" ? inputEl.value : "";
      const fileListCount = Number.isFinite(inputEl?.files?.length)
        ? inputEl.files.length
        : null;
      const originalFile = inputEl?.files?.[0] || null;
      const inputFileAtReceipt = inputEl?.files?.[0] || null;

      // KICK-OFF-THEN-AWAIT: for image selections, start reading the picker-
      // provided File synchronously on the native change event — *before* any
      // setState, selectionId bump, or awaited log. The returned promise is
      // stored (not awaited yet) so React state updates downstream can proceed
      // in parallel while the read settles against the still-valid Android
      // Photo Picker handle. Awaiting earlier would let the handle get revoked
      // under us; dispatching state earlier would force React to re-render
      // before we've captured bytes.
      const eagerReadStartedAt =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      let eagerReadPromise = null;
      if (
        type === "image" &&
        originalFile &&
        typeof originalFile.arrayBuffer === "function"
      ) {
        try {
          eagerReadPromise = originalFile.arrayBuffer();
        } catch (eagerKickoffError) {
          eagerReadPromise = Promise.reject(eagerKickoffError);
        }
      }

      const selectionId = selectionIdRef.current + 1;
      selectionIdRef.current = selectionId;
      const isSelectionCurrent = () => selectionIdRef.current === selectionId;

      if (snapshotInFlightRef.current) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "snapshot_interrupted_new_selection",
          previousSelectionId: snapshotInFlightRef.current.selectionId ?? null,
          nextSelectionId: selectionId,
        });
      }

      console.log("[CATCH_IMG_DEBUG]", {
        stage: "file_change_received",
        selectionId,
        inputId,
        fileListCount,
        inputValueBeforeClear,
        name: typeof originalFile?.name === "string" ? originalFile.name : "",
        type: typeof originalFile?.type === "string" ? originalFile.type : "",
        size: Number.isFinite(originalFile?.size) ? originalFile.size : null,
        lastModified: Number.isFinite(originalFile?.lastModified)
          ? originalFile.lastModified
          : null,
        inputAccept,
        inputCapture,
        inputMultiple,
        webkitRelativePath:
          typeof originalFile?.webkitRelativePath === "string"
            ? originalFile.webkitRelativePath
            : "",
        eventIsTrusted: typeof event?.isTrusted === "boolean" ? event.isTrusted : null,
        ...describeSnapshotFileObject(originalFile),
      });

      if (!originalFile) {
        return;
      }

      if (submitTransactionRef.current?.status === "submitting") {
        setSubmitStatus(
          t("catch_submit_in_progress", {
            defaultValue: "Upload already in progress.",
          }),
          "info"
        );
        if (inputEl && typeof inputEl.value === "string") {
          inputEl.value = "";
          console.log("[CATCH_IMG_DEBUG]", {
            stage: "file_input_cleared",
            selectionId,
            inputId,
            clearedAfterSnapshot: false,
            reason: "submit_in_progress",
          });
        }
        return;
      }

      snapshotInFlightRef.current = {
        selectionId,
        inputId,
        startedAt: Date.now(),
        inputEl,
        receivedFile: originalFile,
      };

      try {
        // Dispatch pending state synchronously BEFORE awaiting the eager read
        // so React can render the "pending" UI while the ArrayBuffer is still
        // materializing against the picker handle. The pending state initially
        // tracks the raw picker File; we swap in the stabilized File below if
        // the eager read succeeds.
        const pendingState = {
          selectionId,
          selectedFile: null,
          originalFile,
          snapshotFile: null,
          snapshotStatus: "pending",
          uploadFile: null,
          uploadFilename: "",
          uploadMimeType: "",
          mediaType: type,
          previewUrl: "",
          previewSource: "",
          previewStatus: "idle",
          exifStatus: "idle",
          submitReady: false,
          blockingError: "",
          blockingErrorCode: "",
          nonBlockingMessage: "",
        };

        setMediaStateForSelection(pendingState, { persistDraft: false });
        uploadAssetRef.current = {
          selectionId,
          snapshotStatus: "pending",
          originalFile,
          snapshotFile: null,
          previewStatus: "idle",
          previewBlobUrl: "",
          exifStatus: "idle",
        };

        // Consume the eager-read promise kicked off synchronously at the top
        // of this handler. Outcomes:
        //   - success → sniff MIME, detect HEIC, build stabilized File
        //   - HEIC → reject selection, show user-facing message, return early
        //   - failure → log and fall through to the main snapshot pipeline,
        //     which will retry using its strategy cascade (and, on Android,
        //     will short-circuit to `snapshot_failed_android_picker_handle`)
        let eagerStabilizedFile = null;
        let eagerResolvedMime = "";
        if (eagerReadPromise) {
          try {
            const buffer = await eagerReadPromise;
            const byteLength = Number.isFinite(buffer?.byteLength)
              ? buffer.byteLength
              : null;
            const elapsedMs =
              (typeof performance !== "undefined" &&
              typeof performance.now === "function"
                ? performance.now()
                : Date.now()) - eagerReadStartedAt;
            eagerResolvedMime = resolveImageMimeTypeFromBuffer(
              originalFile,
              buffer
            );

            if (HEIC_MIME_TYPES.has(eagerResolvedMime)) {
              console.warn("[CATCH_IMG_DEBUG]", {
                stage: "heic_rejected_client_side",
                selectionId,
                inputId,
                name:
                  typeof originalFile?.name === "string"
                    ? originalFile.name
                    : "",
                declaredType:
                  typeof originalFile?.type === "string"
                    ? originalFile.type
                    : "",
                resolvedMime: eagerResolvedMime,
                byteLength,
                elapsedMs: Number.isFinite(elapsedMs)
                  ? Math.round(elapsedMs)
                  : null,
                userAgent: getCurrentUserAgent(),
              });

              if (!isSelectionCurrent()) {
                console.warn("[CATCH_IMG_DEBUG]", {
                  stage: "heic_rejected_stale_selection",
                  selectionId,
                  currentSelectionId: selectionIdRef.current ?? null,
                  inputId,
                });
                return;
              }

              rejectImageSelectionWithoutSnapshot(selectionId, {
                snapshotStatus: "failed",
                failureCode: CATCH_IMAGE_HEIC_UNSUPPORTED_ERROR_CODE,
                failureReason: "heic_unsupported_client_side",
                message: CATCH_IMAGE_HEIC_UNSUPPORTED_ERROR_MESSAGE,
                reason: "heic_rejected_client_side",
              });
              finalizeSelectionInputClear(
                inputEl,
                inputId,
                selectionId,
                {
                  status: "failed",
                  snapshotFile: null,
                  originalFile: null,
                  readStrategy: "",
                },
                { reason: "heic_rejected_client_side" }
              );
              return;
            }

            eagerStabilizedFile = buildStabilizedFileFromBuffer(
              originalFile,
              buffer,
              eagerResolvedMime
            );

            console.log("[CATCH_IMG_DEBUG]", {
              stage: "snapshot_eager_read_success",
              selectionId,
              inputId,
              name:
                typeof originalFile?.name === "string"
                  ? originalFile.name
                  : "",
              declaredType:
                typeof originalFile?.type === "string"
                  ? originalFile.type
                  : "",
              resolvedMime: eagerResolvedMime,
              originalSize: Number.isFinite(originalFile?.size)
                ? originalFile.size
                : null,
              stabilizedSize: Number.isFinite(eagerStabilizedFile?.size)
                ? eagerStabilizedFile.size
                : null,
              byteLength,
              elapsedMs: Number.isFinite(elapsedMs)
                ? Math.round(elapsedMs)
                : null,
            });

            // Fire-and-forget EXIF GPS extraction off the stabilized buffer.
            // Runs in a microtask so it never blocks the snapshot pipeline;
            // apply-time guards in setCoordsFromExifIfEmpty ensure user-typed
            // coords always win even if their keystroke commits between the
            // extract-start and the apply. Silent by design on every failure.
            if (buffer && eagerResolvedMime) {
              const exifSelectionId = selectionId;
              const exifByteLength = Number.isFinite(buffer?.byteLength)
                ? buffer.byteLength
                : null;
              const round5 = (v) => Math.round(v * 1e5) / 1e5;
              Promise.resolve().then(async () => {
                console.log("[CATCH_IMG_DEBUG]", {
                  stage: "exif_gps_extract_start",
                  selectionId: exifSelectionId,
                  mimeType: eagerResolvedMime,
                  byteLength: exifByteLength,
                });
                const result = await extractGpsCoordsFromImageBuffer(buffer, {
                  mimeType: eagerResolvedMime,
                });
                if (selectionIdRef.current !== exifSelectionId) {
                  return;
                }
                if (!result.ok) {
                  console.log("[CATCH_IMG_DEBUG]", {
                    stage: "exif_gps_extract_skipped",
                    selectionId: exifSelectionId,
                    reason: result.reason,
                  });
                  if (result.reason === "non_finite" && result.debug) {
                    console.log("[CATCH_IMG_DEBUG]", {
                      stage: "exif_gps_non_finite_debug",
                      selectionId: exifSelectionId,
                      ...result.debug,
                    });
                  }
                  return;
                }
                console.log("[CATCH_IMG_DEBUG]", {
                  stage: "exif_gps_extract_success",
                  selectionId: exifSelectionId,
                  latitude: round5(result.latitude),
                  longitude: round5(result.longitude),
                  latRef: result.latRef ?? null,
                  lonRef: result.lonRef ?? null,
                  source: result.source || "gps_ifd",
                });
                const applied = setCoordsFromExifIfEmpty?.({
                  latitude: result.latitude,
                  longitude: result.longitude,
                });
                if (applied) {
                  console.log("[CATCH_IMG_DEBUG]", {
                    stage: "exif_gps_applied_to_location",
                    selectionId: exifSelectionId,
                    latitude: round5(result.latitude),
                    longitude: round5(result.longitude),
                  });
                } else {
                  console.log("[CATCH_IMG_DEBUG]", {
                    stage: "exif_gps_not_applied_field_occupied",
                    selectionId: exifSelectionId,
                  });
                }
              });
            }
          } catch (eagerReadError) {
            const elapsedMs =
              (typeof performance !== "undefined" &&
              typeof performance.now === "function"
                ? performance.now()
                : Date.now()) - eagerReadStartedAt;
            console.warn("[CATCH_IMG_DEBUG]", {
              stage: "snapshot_eager_read_failed",
              selectionId,
              inputId,
              name:
                typeof originalFile?.name === "string"
                  ? originalFile.name
                  : "",
              type:
                typeof originalFile?.type === "string"
                  ? originalFile.type
                  : "",
              originalSize: Number.isFinite(originalFile?.size)
                ? originalFile.size
                : null,
              elapsedMs: Number.isFinite(elapsedMs)
                ? Math.round(elapsedMs)
                : null,
              errorName:
                typeof eagerReadError?.name === "string"
                  ? eagerReadError.name
                  : "",
              errorMessage:
                typeof eagerReadError?.message === "string"
                  ? eagerReadError.message
                  : "",
            });
          }

          if (!isSelectionCurrent()) {
            console.warn("[CATCH_IMG_DEBUG]", {
              stage: "snapshot_eager_read_stale_selection",
              selectionId,
              currentSelectionId: selectionIdRef.current ?? null,
              inputId,
            });
            return;
          }
        }

        const downstreamFile = eagerStabilizedFile || originalFile;

        if (eagerStabilizedFile) {
          snapshotInFlightRef.current = {
            ...snapshotInFlightRef.current,
            receivedFile: eagerStabilizedFile,
          };
          uploadAssetRef.current = {
            ...uploadAssetRef.current,
            originalFile: eagerStabilizedFile,
          };
        }

        const snapshotPromise = snapshotSelectedFile(
          downstreamFile,
          {
            selectionId,
            inputId,
            inputAccept,
            inputCapture,
            inputMultiple,
            inputValueBeforeClear,
            fileListCountAtReceipt: fileListCount,
            receivedFile: downstreamFile,
            inputStillExistsAtReceipt: Boolean(inputEl),
            inputIsConnectedAtReceipt:
              typeof inputEl?.isConnected === "boolean" ? inputEl.isConnected : null,
            inputFileMatchesReferenceAtReceipt: Boolean(inputFileAtReceipt)
              ? inputFileAtReceipt === originalFile
              : false,
            inputFileNameAtReceipt:
              typeof inputFileAtReceipt?.name === "string"
                ? inputFileAtReceipt.name
                : "",
            inputFileSizeAtReceipt: Number.isFinite(inputFileAtReceipt?.size)
              ? inputFileAtReceipt.size
              : null,
            inputFileTypeAtReceipt:
              typeof inputFileAtReceipt?.type === "string"
                ? inputFileAtReceipt.type
                : "",
            liveInputInspectionSkipped: true,
            fromChangeHandler: true,
            snapshotReadStartedImmediately: true,
            eagerStabilized: Boolean(eagerStabilizedFile),
            eagerResolvedMime,
            requestedType: type,
            userAgent: getCurrentUserAgent(),
          },
          () => selectionIdRef.current
        );

        const snapshotResult = await snapshotPromise;

        if (!isSelectionCurrent()) {
          console.warn("[CATCH_IMG_DEBUG]", {
            stage: "snapshot_result_ignored_stale_selection",
            selectionId,
            currentSelectionId: selectionIdRef.current ?? null,
            inputId,
          });
          return;
        }

        resetSelectionDerivedMediaState();
        setSubmitStatus("", "info");

        const selectedMediaBundle = {
          ...(snapshotResult || {}),
          originalFile: downstreamFile,
        };

        if (!selectedMediaBundle.originalFile && !selectedMediaBundle.snapshotFile) {
          console.warn("[CATCH_IMG_DEBUG]", {
            stage: "snapshot_failed_without_fallback_source",
            selectionId,
            inputId,
            inputValueBeforeClear,
            snapshotStatus: selectedMediaBundle?.status || "failed",
            hasSnapshotFile: Boolean(selectedMediaBundle?.snapshotFile),
            hasOriginalFile: Boolean(selectedMediaBundle?.originalFile),
            failureCode: selectedMediaBundle?.failureCode || "",
            failureReason: selectedMediaBundle?.failureReason || "",
          });

          if (type === "image") {
            rejectImageSelectionWithoutSnapshot(selectionId, {
              snapshotStatus: selectedMediaBundle?.status || "failed",
              failureCode: selectedMediaBundle?.failureCode || "",
              failureReason: selectedMediaBundle?.failureReason || "",
              message:
                selectedMediaBundle?.failureMessage ||
                CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
              reason: "handle_image_upload_missing_source",
            });
            finalizeSelectionInputClear(
              inputEl,
              inputId,
              selectionId,
              {
                status: selectedMediaBundle?.status || "failed",
                snapshotFile: null,
                originalFile: null,
                readStrategy: selectedMediaBundle?.readStrategy || "",
              },
              {
                reason: "image_selection_rejected",
              }
            );
            return;
          }

          uploadAssetRef.current = {
            selectionId,
            snapshotStatus: "failed",
            originalFile: null,
            snapshotFile: null,
            previewStatus: "failed",
            previewBlobUrl: "",
            exifStatus: "failed",
          };
          setMediaStateForSelection({
            selectionId,
            selectedFile: null,
            originalFile: null,
            snapshotFile: null,
            snapshotStatus: "failed",
            uploadFile: null,
            uploadFilename: "",
            uploadMimeType: "",
            mediaType: type,
            previewUrl: "",
            previewSource: "",
            previewStatus: "failed",
            exifStatus: "failed",
            submitReady: false,
            blockingError: "",
            blockingErrorCode: "",
            nonBlockingMessage: "",
          });
          finalizeSelectionInputClear(inputEl, inputId, selectionId, selectedMediaBundle, {
            reason: "media_missing",
          });
          setSubmitPhase(SUBMIT_PHASES.FAILED, { reason: "media_missing" });
          return;
        }

        const selectedUploadFile =
          selectedMediaBundle.snapshotFile || selectedMediaBundle.originalFile;
        const normalizedType = inferSelectedMediaType(selectedUploadFile, type);
        const imageSelectionNeedsSnapshot =
          normalizedType === "image" &&
          (selectedMediaBundle.status !== "success" ||
            !selectedMediaBundle.snapshotFile);

        if (imageSelectionNeedsSnapshot) {
          const repickMessage =
            selectedMediaBundle?.failureMessage ||
            CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE;

          rejectImageSelectionWithoutSnapshot(selectionId, {
            snapshotStatus: selectedMediaBundle?.status || "failed",
            failureCode: selectedMediaBundle?.failureCode || "",
            failureReason: selectedMediaBundle?.failureReason || "",
            message: repickMessage,
            reason: "handle_image_upload_without_snapshot",
          });
          finalizeSelectionInputClear(
            inputEl,
            inputId,
            selectionId,
            {
              status: selectedMediaBundle?.status || "failed",
              snapshotFile: null,
              originalFile: null,
              readStrategy: selectedMediaBundle?.readStrategy || "",
            },
            {
              reason: "image_selection_rejected",
            }
          );
          return;
        }

        if (
          selectedMediaBundle.status !== "success" ||
          !selectedMediaBundle.snapshotFile
        ) {
          console.warn("[CATCH_IMG_DEBUG]", {
            stage: "snapshot_failed_nonblocking_fallback",
            selectionId,
            inputId,
            inputValueBeforeClear,
            snapshotStatus: selectedMediaBundle?.status || "failed",
            hasSnapshotFile: Boolean(selectedMediaBundle?.snapshotFile),
            hasOriginalFile: Boolean(selectedMediaBundle?.originalFile),
            failureCode: selectedMediaBundle?.failureCode || "",
            failureReason: selectedMediaBundle?.failureReason || "",
          });
        }

        try {
          console.log("[CATCH_IMG_DEBUG]", {
            stage: "select",
            requestedType: type,
            name:
              typeof selectedUploadFile?.name === "string"
                ? selectedUploadFile.name
                : "",
            type:
              typeof selectedUploadFile?.type === "string"
                ? selectedUploadFile.type
                : "",
            size: Number.isFinite(selectedUploadFile?.size)
              ? selectedUploadFile.size
              : null,
            lastModified: Number.isFinite(selectedUploadFile?.lastModified)
              ? selectedUploadFile.lastModified
              : null,
            extension: getFilenameExtension(
              typeof selectedUploadFile?.name === "string"
                ? selectedUploadFile.name
                : ""
            ),
            userAgent:
              typeof navigator !== "undefined" &&
              typeof navigator.userAgent === "string"
                ? navigator.userAgent
                : "",
            hasCreateImageBitmap: typeof createImageBitmap === "function",
            hasFile: typeof File === "function",
            hasOffscreenCanvas: typeof OffscreenCanvas !== "undefined",
            devicePixelRatio:
              typeof window !== "undefined" &&
              Number.isFinite(window.devicePixelRatio)
                ? window.devicePixelRatio
                : null,
          });
        } catch (debugLogError) {
          console.warn("[CATCH_IMG_DEBUG] select log failed", debugLogError);
        }

        if (type === "image" && user?.requiresExifProcessingConsent) {
          requestExifConsent?.({
            snapshotBundle: selectedMediaBundle,
            type: normalizedType,
            selectionId,
          });
          finalizeSelectionInputClear(inputEl, inputId, selectionId, selectedMediaBundle, {
            reason: "exif_consent_pending",
          });
          return;
        }

        await applySelectedMedia(selectedMediaBundle, normalizedType, selectionId);
        finalizeSelectionInputClear(inputEl, inputId, selectionId, selectedMediaBundle, {
          reason: "selection_ready",
        });
      } finally {
        snapshotInFlightRef.current = null;
      }
    },
    [
      applySelectedMedia,
      finalizeSelectionInputClear,
      rejectImageSelectionWithoutSnapshot,
      requestExifConsent,
      resetSelectionDerivedMediaState,
      selectionIdRef,
      setCoordsFromExifIfEmpty,
      setMediaStateForSelection,
      setSubmitPhase,
      setSubmitStatus,
      snapshotInFlightRef,
      submitTransactionRef,
      t,
      uploadAssetRef,
      user,
    ]
  );

  return {
    handleImageUpload,
  };
}
